import React, { useState, useEffect, useLayoutEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { db } from "../utility/firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  updateDoc,
  arrayRemove,
  doc,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { apiUrl, myFetchPost, webSiteUrl, getChatRoomName } from "../utility";
import ProfilePic from "../resource/profile_pic.png";
import { Button, Form, Modal } from "react-bootstrap";

import MainScreenComponent from "../components/MainScreenComponent";
import ChatRoomHeader from "../components/ChatRoomHeader";
import ConversationList from "../components/ConversationList";
import MessageList from "../components/MessageList";
import MessageInput from "../components/MessageInput";
import GoogleAds from "../components/GoogleAds";

import "react-photoswipe/lib/photoswipe.css";
import { PhotoSwipe } from "react-photoswipe";
import FullScreenSpinner from "../components/FullScreenSpinner";
import WarningModal from "../components/WarningModal";
import NotificationRequestModal from "../components/NotificationRequestModal";

const ChatScreen = (props) => {
  const { currentUser } = useAuth();

  const params = useParams();

  const [searchParams, setSearchParams] = useSearchParams("");

  /* parameters from react-router-dom */
  const { chatRoom, itemId, listItemId } = params;
  const currentChatRoomId = chatRoom;

  /* basic info */
  const [myUserInfo, setMyUserInfo] = useState({});
  const [itemData, setItemData] = useState({});
  const [chatUserData, setChatUserData] = useState({});
  const [currentChatRoom, setCurrentChatRoom] = useState(null);
  const [chatRoomMeta, setChatRoomMeta] = useState({});
  const [prefillText, setPrefilText] = useState('');
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showOfferModalReady, setShowOfferModalReady] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  const [showNotificationRequestModal, setShowNotificationRequestModal] =
    useState(true);

  const [phoneChat, setPhoneChat] = useState(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [isOpen, setIsOpen] = useState({
    open: false,
    items: [{}],
  });

  const navigate = useNavigate();

  /* determine the layout, desktop or phone */
  useLayoutEffect(() => {
    if (window.innerWidth <= 992) {
      setPhoneChat(true);
    } else {
      setPhoneChat(false);
    }
    window.addEventListener("resize", () => {
      if (window.innerWidth <= 992) {
        setPhoneChat(true);
      } else {
        setPhoneChat(false);
      }
    });
    return () => {
      window.removeEventListener("resize", () => { });
    };
  }, []);

  /* get current user info */
  useEffect(() => {
    currentUser
      .getIdToken()
      .then(async (data) => {
        const requestData = {};
        return myFetchPost(`${apiUrl}/chat/getuser`, requestData, {
          Authorization: "Bearer " + data,
        });
      })
      .then((data) => {
        setMyUserInfo(data);
      })
      .catch((err) => { });
  }, []);

  /* we are in new chat screen */
  useEffect(() => {
    if (itemId != undefined) {
      /* we will also get the item & user info from our API endpoint */
      currentUser.getIdToken().then(async (data) => {
        const requestData = {
          itemID: itemId,
        };
        myFetchPost(`${apiUrl}/chat/getnewchat`, requestData, {
          Authorization: "Bearer " + data,
        })
          .then((data) => {
            if (data.result != "success") {
              throw data.message;
            }

            if (data.chatRoomMeta.require_verification) {
              setShowVerificationDialog(true);
              return 'showVerificationDialog';
            }

            setItemData(data.itemInfo);
            setChatRoomMeta(data.chatRoomMeta);

            if (data.chatRoomMeta?.suggested_keywords) {
              // current user is buyer
              if (data.chatRoomMeta?.suggested_keywords?.buyer?.not_offered) {
                setSuggestedKeywords(data.chatRoomMeta?.suggested_keywords?.buyer?.not_offered);
              }
            }

            if (data.chatRoomMeta?.warning_dialog_message) {
              setShowWarningModal(true);
            }
            return data.membersInfo[data.itemInfo.userID.toString()];
          })
          .then(async (data) => {
            if (data == undefined) {
              throw "不能開始對話頁面";
            }
            if (data == 'showVerificationDialog') {
              return true;
            }

            setChatUserData(data);

            const chatRoomName = getChatRoomName(
              currentUser.uid,
              data.uid,
              itemId
            );

            /* also check if the chat room exists, if exist redirect to that chat room */
            const chatRoomRef = query(
              collection(db, "chatRooms"),
              where("members", "array-contains", currentUser.uid),
              where("chatRoomName", "==", chatRoomName)
            );
            const querySnapshot = await getDocs(chatRoomRef);
            querySnapshot.forEach((doc) => {
              if (searchParams.get("offer") != null) {
                navigate(`/${doc.id}?offer`, { replace: true });
              } else {
                navigate(`/${doc.id}`, { replace: true });
              }
              return false;
            });
            setShowOfferModalReady(true);
          })
          .catch((err) => {
            alert(err);
            navigate(-1);
          });
      });
    }
    return () => {
      setItemData({});
      setChatUserData({});
    };
  }, [itemId]);

  /* we have currentChatRoomId, we get chatRoom details & messages */
  useEffect(() => {
    if (currentChatRoomId != undefined) {
      let chatRoomMetaLoaded = false;
      setPrefilText('');

      /* chatRoom details realtime listener */
      const unSubChatRoom = onSnapshot(
        doc(db, "chatRooms", currentChatRoomId),
        { includeMetadataChanges: true },
        (chatRoomDoc) => {
          const chatRoomData = chatRoomDoc.data();
          if (chatRoomData == undefined) {
            navigate("/", { replace: true });
          } else {
            chatRoomData["currentChatRoomId"] = currentChatRoomId;
            setCurrentChatRoom(chatRoomData);
            setItemData((prevData) => {
              return {
                ...chatRoomData.itemInfo,
                ...prevData,
              };
            });

            if (!chatRoomMetaLoaded) {
              chatRoomMetaLoaded = true;

              /* we will also get the item & user info from our API endpoint */
              currentUser.getIdToken().then(async (data) => {
                const tokenData = data;
                const requestData = {
                  itemID: chatRoomData.itemInfo.itemId,
                  users: chatRoomData.members,
                  chatRoomID: currentChatRoomId
                };
                myFetchPost(`${apiUrl}/chat/initroom`, requestData, {
                  Authorization: "Bearer " + tokenData,
                })
                  .then((data) => {
                    if (data.result != 'success') {
                      throw new Error(data.message);
                    }
                    const targetUserId = chatRoomData.members.find(
                      (p) => p != currentUser.uid
                    );
                    const thisChatUserData = data.membersInfo[targetUserId];
                    setItemData(data.itemInfo);
                    setChatUserData(thisChatUserData);

                    setChatRoomMeta(data.chatRoomMeta);

                    if (data.chatRoomMeta?.suggested_keywords) {
                      //determine the final suggested keywords
                      if (chatRoomData.createdUser == currentUser.uid) {
                        // current user is buyer
                        if (chatRoomData.lastOffer?.offeredStatus == "offered") {
                          if (data.chatRoomMeta?.suggested_keywords?.buyer?.offered) {
                            setSuggestedKeywords(data.chatRoomMeta?.suggested_keywords?.buyer?.offered);
                          }
                        } else if (chatRoomData.lastOffer?.offeredStatus == "" || chatRoomData.lastOffer?.offeredStatus == "waiting") {
                          if (data.chatRoomMeta?.suggested_keywords?.buyer?.not_offered) {
                            setSuggestedKeywords(data.chatRoomMeta?.suggested_keywords?.buyer?.not_offered);
                          }
                        } else if (chatRoomData.lastOffer?.offeredStatus == "accepted") {
                          if (data.chatRoomMeta?.suggested_keywords?.buyer?.accepted) {
                            if (!(chatRoomData.reviewStatus?.createdUserReviewStatus && chatRoomData.reviewStatus?.itemOwnerReviewStatus)) {
                              setSuggestedKeywords(data.chatRoomMeta?.suggested_keywords?.buyer?.accepted);
                            }
                          }
                        }
                      } else {
                        // current user is seller
                        if (chatRoomData.lastOffer?.offeredStatus == "offered") {
                          if (data.chatRoomMeta?.suggested_keywords?.seller?.offered) {
                            setSuggestedKeywords(data.chatRoomMeta?.suggested_keywords?.seller?.offered);
                          }
                        } else if (chatRoomData.lastOffer?.offeredStatus == "" || chatRoomData.lastOffer?.offeredStatus == "waiting") {
                          if (data.chatRoomMeta?.suggested_keywords?.seller?.not_offered) {
                            setSuggestedKeywords(data.chatRoomMeta?.suggested_keywords?.seller?.not_offered);
                          }
                        } else if (chatRoomData.lastOffer?.offeredStatus == "accepted") {
                          if (data.chatRoomMeta?.suggested_keywords?.seller?.accepted) {
                            if (!(chatRoomData.reviewStatus?.createdUserReviewStatus && chatRoomData.reviewStatus?.itemOwnerReviewStatus)) {
                              setSuggestedKeywords(data.chatRoomMeta?.suggested_keywords?.seller?.accepted);
                            }
                          }
                        }
                      }
                    }

                    /* if user avatar doesn't match the data.avatar */
                    if (
                      thisChatUserData?.avatar !=
                      chatRoomData.membersInfo[targetUserId].avatar ||
                      thisChatUserData?.username !=
                      chatRoomData.membersInfo[targetUserId].username ||
                      (chatRoomData.membersInfo[targetUserId].suspended == undefined && thisChatUserData?.suspended) ||
                      (chatRoomData.membersInfo[targetUserId].suspended != undefined && thisChatUserData?.suspended != chatRoomData.membersInfo[targetUserId].suspended)
                    ) {
                      const requestData = {
                        targetUser: thisChatUserData.uid,
                      };
                      myFetchPost(`${apiUrl}/chat/refresh`, requestData, {
                        Authorization: "Bearer " + tokenData,
                      })
                        .then((data) => { })
                        .catch((err) => {
                          alert(err);
                        });
                    }
                    setShowOfferModalReady(true);

                    if (data.chatRoomMeta?.warning_dialog_message) {
                      setShowWarningModal(true);
                    }
                  })
                  .catch((err) => {
                    //go back
                    alert(err);
                    navigate(-1);
                  });
              });
            }
          }
        }
      );

      return () => {
        setCurrentChatRoom(null);
        setItemData({});
        setChatUserData({});
        unSubChatRoom();
      };
    }
  }, [currentChatRoomId]);

  /* just for testing */
  const handleNewChat = async (itemId) => {
    navigate(`/new/${itemId}`);
  };

  /* handleRemoveChat */
  const handleRemoveChat = async () => {
    /* need to present a Modal Dialog Asking the user to confirm */
    try {
      setShowRemoveModal(false);
      if (window.confirm('確定要刪除此對話?')) {
        await updateDoc(doc(db, "chatRooms", currentChatRoomId), {
          activeMembers: arrayRemove(currentUser.uid),
        });
        navigate("/");
      }
    } catch (error) {
      alert('未能成功刪除對話, 請回報此問題');
    }
  };

  /* final step to show the layout */
  let messageListStyle = {};

  if (phoneChat) {
    if (props.showMainScreen == true) {
      messageListStyle = {
        zIndex: 998,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#fff",
      };
    } else {
      messageListStyle = {
        zIndex: 1000,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#fff",
      };
    }
  } else {
    messageListStyle = {
      position: "relative",
    };
  }

  // let shortcutMessage = null;
  // if (currentChatRoom != null) {
  //   if (currentChatRoom.reviewStatus?.createdUserReviewStatus && currentChatRoom.reviewStatus?.itemOwnerReviewStatus) {
  //     // nothing here
  //   } else {
  //     if (currentUser.uid == currentChatRoom?.createdUser) { /* i make a offer */
  //       if (currentChatRoom.lastOffer?.offeredStatus == "offered") {
  //         shortcutMessage = <div style={{ padding: "10px 0px" }}>
  //           <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('邊度交收方便?')}>邊度交收方便?</div>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('請留個電話')}>請留個電話</div>
  //           </div>
  //         </div>
  //       } else if (currentChatRoom.lastOffer?.offeredStatus == "accepted") {
  //         shortcutMessage = <div style={{ padding: "10px 0px" }}>
  //           <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('邊度交收方便?')}>邊度交收方便?</div>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('請留個電話')}>請留個電話</div>
  //           </div>
  //         </div>
  //       } else if (currentChatRoom.lastOffer?.offeredStatus == "rejected") {
  //       } else {
  //         shortcutMessage = <div style={{ padding: "10px 0px" }}>
  //           <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('邊度交收方便?')}>邊度交收方便?</div>
  //           </div>
  //         </div>
  //       }
  //     } else { /* i own this item */
  //       if (currentChatRoom.lastOffer?.offeredStatus == "offered") {
  //         shortcutMessage = <div style={{ padding: "10px 0px" }}>
  //           <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('邊度交收方便?')}>邊度交收方便?</div>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('請留個電話')}>請留個電話</div>
  //           </div>
  //         </div>
  //       } else if (currentChatRoom.lastOffer?.offeredStatus == "accepted") {
  //         shortcutMessage = <div style={{ padding: "10px 0px" }}>
  //           <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('邊度交收方便?')}>邊度交收方便?</div>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('請留個電話')}>請留個電話</div>
  //           </div>
  //         </div>
  //       } else if (currentChatRoom.lastOffer?.offeredStatus == "rejected") {
  //         shortcutMessage = <div style={{ padding: "10px 0px" }}>
  //           <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('Sorry 價格不合')}>Sorry 價格不合</div>
  //           </div>
  //         </div>
  //       } else {
  //         shortcutMessage = <div style={{ padding: "10px 0px" }}>
  //           <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
  //             <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('請出價')}>請出價</div>
  //           </div>
  //         </div>
  //       }
  //     }

  //   }
  // }

  const shortcutMessage = suggestedKeywords.length > 0 ? (
    <div style={{ padding: "10px 0px" }}>
      <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
        {
          suggestedKeywords.map((data, index) => (
            <div key={index} style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText(data)}>{data}</div>
          ))
        }
      </div>
    </div>
  ) : null;

  return (
    <div className="chatAppContainer">
      <div
        className={
          props.showMainScreen == true
            ? "conversationListWrap mainScreen"
            : "conversationListWrap"
        }
        style={phoneChat ? { zIndex: 999 } : {}}
      >
        <div className="conversationListHeader">
          <a href="/users/avatar.php" target="_blank">
            <img
              src={
                myUserInfo.userInfo?.avatar
                  ? webSiteUrl + myUserInfo.userInfo.avatar
                  : ProfilePic
              }
              className="avatar"
            />
          </a>
          <span>你好 {myUserInfo.userInfo?.username}!</span>
          {/* <span>你好 {currentUser.displayName}</span> */}
        </div>
        <ConversationList
          listItemId={listItemId}
          phoneChat={phoneChat}
          currentUser={currentUser}
          currentChatRoomId={currentChatRoomId}
          setUnReadCounterHere={props.setUnReadCounterHere}
        />
      </div>
      {props.showMainScreen == true ? (
        <div className="messageListWrap" style={messageListStyle}>
          <MainScreenComponent handleNewChat={handleNewChat} />
        </div>
      ) : (
        <>
          <div className="messageListWrap" style={messageListStyle}>
            <ChatRoomHeader
              listItemId={listItemId}
              currentUser={currentUser}
              itemData={itemData}
              setItemData={setItemData}
              currentChatRoom={currentChatRoom}
              currentChatRoomId={currentChatRoomId}
              chatUserData={chatUserData}
              unReadCounter={props.unReadCounter}
              setIsSendingMessage={setIsSendingMessage}
              itemId={itemId}
              offer={searchParams.get("offer")}
              chatRoomMeta={chatRoomMeta}
              showWarningModal={showWarningModal}
              showOfferModalReady={showOfferModalReady}
            />
            <MessageList
              currentUser={currentUser}
              chatUserData={chatUserData}
              currentChatRoomId={currentChatRoomId}
              setIsOpen={setIsOpen}
              itemId={itemId}
              suggestedKeywords={suggestedKeywords}
              setSuggestedKeywords={setSuggestedKeywords}
            />
            {chatRoomMeta?.warning_message ? (
              <div
                style={{
                  textAlign: "center",
                  color: "red",
                  fontSize: "12px",
                  paddingTop: "5px",
                  paddingBottom: "5px",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: chatRoomMeta?.warning_message,
                  }}
                />
              </div>
            ) : null}
            {chatRoomMeta?.chat_disabled ? (
              <div className="chatDisabled">此對話已被暫停</div>
            ) : (
              <>
                {/* {itemId != undefined && <div style={{ padding: "10px 0px" }}>
                  <div style={{ overflow: "auto", whiteSpace: 'nowrap', paddingLeft: "10px" }}>
                    <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('請問賣咗未?')}>請問賣咗未?</div>
                    <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('邊度交收?')}>邊度交收?</div>
                    <div style={{ display: "inline-block", border: "solid 0.5px #666", padding: "5px 15px", borderRadius: "15px", fontSize: '13px', marginRight: 10 }} onClick={() => setPrefilText('我有興趣呀!')}>我有興趣呀!</div>
                  </div>
                </div>
                } */}
                {
                  shortcutMessage
                }
                <MessageInput
                  prefillText={prefillText}
                  currentUser={currentUser}
                  chatUserData={chatUserData}
                  currentChatRoomId={currentChatRoomId}
                  itemId={itemId}
                  itemData={itemData}
                  setIsSendingMessage={setIsSendingMessage}
                  setUploadPercentage={setUploadPercentage}
                />
              </>
            )}
          </div>
          {phoneChat == false && (
            <div className="adsColumnWrap" style={{ overflowY: "hidden" }}>
              <GoogleAds width={300} height={600} refreshTime={30000} />
            </div>
          )}
        </>
      )}
      <PhotoSwipe
        isOpen={isOpen.open}
        items={isOpen.items}
        // options={{
        // }}
        onClose={() => {
          setIsOpen({
            open: false,
            items: [{}],
          });
        }}
      />
      <WarningModal
        showWarningModal={showWarningModal}
        setShowWarningModal={setShowWarningModal}
        warningTitle={chatRoomMeta?.warning_dialog_title}
        warningMessage={chatRoomMeta?.warning_dialog_message}
        riskLevel={chatRoomMeta?.risk_level}
        callback={() => {
          if (chatUserData?.suspended) {
            setShowRemoveModal(true);
          }
        }}
      />
      <NotificationRequestModal
        currentUser={currentUser}
        delay={2000}
        remindTimeStep={43200}
        sourceScreen="ChatScreen"
      />
      <FullScreenSpinner
        isSendingMessage={isSendingMessage}
        uploadPercentage={uploadPercentage}
      />
      <Modal
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show={showVerificationDialog}
        onHide={() => {
          setShowVerificationDialog(false);
          navigate(-1);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>免費手機認證</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          為確保二手買賣質素, 請先進行免費手機認證。
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowVerificationDialog(false);
              navigate(-1);
            }}
          >
            關閉
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              window.location.href = 'https://www.dcfever.com/users/verification.php';
            }}
          >
            立即認證
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Remove the chatRoom */}
      <Modal
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show={showRemoveModal}
        onHide={() => {
          setShowRemoveModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>刪除對話</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          由於對話中的會員已被封鎖，此對話已被暫停。你可以選擇永久刪除此對話。
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRemoveModal(false);
            }}
          >
            關閉
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              handleRemoveChat();
            }}
          >
            立即刪除對話
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChatScreen;
